import React, { useState, useEffect, useCallback } from 'react';
import { ServiceProvider, ServiceProviderCreate, ServiceProviderUpdate } from '../../types/serviceProvider';
import { getServiceProviders, createServiceProvider, updateServiceProvider, deleteServiceProvider } from '../../services/api';

interface ServiceProviderManagementProps {
  bcpId: number;
  onServiceProvidersChange?: (providers: ServiceProvider[]) => void;
}

const ServiceProviderManagement: React.FC<ServiceProviderManagementProps> = ({ bcpId, onServiceProvidersChange }) => {
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [formData, setFormData] = useState<ServiceProviderCreate>({
    bcp_id: bcpId,
    provider_type: 'IT' as 'IT' | 'Insurance' | 'Utilities' | 'Emergency' | 'Legal',
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    services_provided: '',
    contract_number: '',
    emergency_contact: '',
    emergency_phone: '',
    is_emergency_contact: false,
    criticality: 'medium',
    response_time: '',
    notes: ''
  });

  const providerTypeOptions = [
    'IT Support', 'Insurance', 'Utilities', 'Emergency Services', 'Legal',
    'Financial', 'Security', 'Maintenance', 'Transportation', 'Other'
  ];

  const criticalityOptions = [
    { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' }
  ];

  const responseTimeOptions = ['24/7', 'Business Hours', 'Emergency Only', 'On Call', 'Scheduled'];

  const loadServiceProviders = useCallback(async () => {
    try {
      const data = await getServiceProviders(bcpId);
      setServiceProviders(data);
      onServiceProvidersChange?.(data);
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  }, [bcpId, onServiceProvidersChange]);

  useEffect(() => {
    loadServiceProviders();
  }, [loadServiceProviders]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        const updateData: ServiceProviderUpdate = {
          provider_type: formData.provider_type,
          name: formData.name,
          contact_person: formData.contact_person,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          services_provided: formData.services_provided,
          contract_number: formData.contract_number,
          emergency_contact: formData.emergency_contact,
          emergency_phone: formData.emergency_phone,
          is_emergency_contact: formData.is_emergency_contact,
          criticality: formData.criticality,
          response_time: formData.response_time,
          notes: formData.notes
        };
        await updateServiceProvider(editingProvider.id, updateData as any);
      } else {
        // Check for duplicates before creating
        const isDuplicate = serviceProviders.some(provider =>
          provider.name.toLowerCase() === formData.name.toLowerCase() &&
          provider.provider_type === formData.provider_type
        );

        if (isDuplicate) {
          alert(`Service provider with name "${formData.name}" and type "${formData.provider_type}" already exists.`);
          return;
        }

        await createServiceProvider(bcpId, formData as any);
      }

      await loadServiceProviders();
      resetForm();
      setIsModalOpen(false);
    } catch (error: any) {
      console.error('Error saving service provider:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail || 'Duplicate service provider detected');
      } else {
        alert('Error saving service provider. Please try again.');
      }
    }
  };

  const handleEdit = (provider: ServiceProvider) => {
    setEditingProvider(provider);
    setFormData({
      bcp_id: bcpId,
      provider_type: provider.provider_type,
      name: provider.name,
      contact_person: provider.contact_person || '',
      phone: provider.phone || '',
      email: provider.email || '',
      address: provider.address || '',
      services_provided: provider.services_provided || '',
      contract_number: provider.contract_number || '',
      emergency_contact: provider.emergency_contact || '',
      emergency_phone: provider.emergency_phone || '',
      is_emergency_contact: provider.is_emergency_contact,
      criticality: provider.criticality,
      response_time: provider.response_time || '',
      notes: provider.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (providerId: number) => {
    if (window.confirm('Are you sure you want to delete this service provider?')) {
      try {
        await deleteServiceProvider(providerId);
        await loadServiceProviders();
      } catch (error) {
        console.error('Error deleting service provider:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      bcp_id: bcpId,
      provider_type: 'IT' as 'IT' | 'Insurance' | 'Utilities' | 'Emergency' | 'Legal',
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      services_provided: '',
      contract_number: '',
      emergency_contact: '',
      emergency_phone: '',
      is_emergency_contact: false,
      criticality: 'medium',
      response_time: '',
      notes: ''
    });
    setEditingProvider(null);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Service Providers</h3>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Add Service Provider
        </button>
      </div>

      {serviceProviders.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No service providers added yet.</p>
          <p className="text-sm">Add service providers for emergency contacts and support services.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criticality</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceProviders.map((provider) => (
                <tr key={provider.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{provider.name}</div>
                        {provider.is_emergency_contact && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Emergency Contact
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{provider.provider_type}</div>
                    {provider.services_provided && (
                      <div className="text-sm text-gray-500">{provider.services_provided}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{provider.contact_person || 'No contact person'}</div>
                    <div className="text-sm text-gray-500">{provider.phone || 'No phone'}</div>
                    <div className="text-sm text-gray-500">{provider.email || 'No email'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      criticalityOptions.find(opt => opt.value === provider.criticality)?.color || 'bg-gray-100 text-gray-800'
                    }`}>
                      {criticalityOptions.find(opt => opt.value === provider.criticality)?.label || provider.criticality}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(provider)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(provider.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProvider ? 'Edit Service Provider' : 'Add Service Provider'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Provider Type *</label>
                    <select
                      required
                      value={formData.provider_type}
                      onChange={(e) => setFormData({ ...formData, provider_type: e.target.value as 'IT' | 'Insurance' | 'Utilities' | 'Emergency' | 'Legal' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type</option>
                      {providerTypeOptions.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                    <input
                      type="text"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Services Provided</label>
                  <textarea
                    value={formData.services_provided}
                    onChange={(e) => setFormData({ ...formData, services_provided: e.target.value })}
                    rows={2}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contract Number</label>
                    <input
                      type="text"
                      value={formData.contract_number}
                      onChange={(e) => setFormData({ ...formData, contract_number: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Criticality</label>
                    <select
                      value={formData.criticality}
                      onChange={(e) => setFormData({ ...formData, criticality: e.target.value as 'critical' | 'high' | 'medium' | 'low' })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      {criticalityOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
                    <input
                      type="text"
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Emergency Phone</label>
                    <input
                      type="tel"
                      value={formData.emergency_phone}
                      onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Response Time</label>
                  <select
                    value={formData.response_time}
                    onChange={(e) => setFormData({ ...formData, response_time: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select response time</option>
                    {responseTimeOptions.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_emergency_contact"
                    checked={formData.is_emergency_contact}
                    onChange={(e) => setFormData({ ...formData, is_emergency_contact: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_emergency_contact" className="ml-2 block text-sm text-gray-900">
                    Emergency Contact
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingProvider ? 'Update' : 'Add'} Service Provider
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceProviderManagement;
